import React, { Component } from "react";
import { Link } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import * as utils from "common/utils";
import Alert from "components/Alert";
import Watermark from "components/Watermark";
import * as domain from "common/domain";
import { connect } from "react-redux";
import { getForestCarbon, clearForestCarbon } from "./_actions";
import { Line, Pie } from "react-chartjs-2";
import ActionLink from "components/ActionLink";
import Panel from "components/Panel/Panel";
import PanelBody from "components/Panel/PanelBody";
import ZendeskLink from "components/Help/ZendeskLink";
import SelectPack from "components/SelectPack";
import FeatureTracker, { FEATURES } from "components/FeatureTracker/FeatureTracker";
import Grid from "components/Grid";
import GridCell from "components/GridCell";

const methaneColour = { name: utils.getColour(4), hex: utils.colourHexIndex[4] };
const n2oColour = { name: utils.getColour(0), hex: utils.colourHexIndex[0] };
const co2Colour = { name: utils.getColour(9), hex: utils.colourHexIndex[9] };

const EmissionsCategory = ({ emissionsCategory, colour, headingClicked, showSections, categoryRefData, hasParent, productionResults, productionTypeRefData }) => {
    const headingClick = (type) => {
        headingClicked(type);
    };
    const label = categoryRefData.find((r) => r.value === emissionsCategory.type) || { text: "Unknown" };
    const isShow = showSections && showSections[emissionsCategory.type];
    const hasChildren = emissionsCategory.type === "EntericMethaneTotal" || (emissionsCategory.children && emissionsCategory.children.length > 0);
    const rowClass = hasParent ? "tr-nested" : "Table-tr--" + colour;
    return (
        <React.Fragment>
            <tr className={rowClass}>
                <td id={emissionsCategory.type + "-label"} colSpan={2}>
                    {hasChildren && (
                        <ActionLink className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween" onClick={(type) => headingClick(emissionsCategory.type)}>
                            {label.text}
                            <i className={`icon icon-arrow-${isShow ? "up" : "down"}`}></i>
                        </ActionLink>
                    )}
                    {!hasChildren && label.text}
                </td>
                <td id={utils.strToId(emissionsCategory.type + "-value")} className="u-textRight">
                    {emissionsCategory.value}
                </td>
            </tr>
            {emissionsCategory.type !== "EntericMethaneTotal" && isShow && hasChildren && emissionsCategory.children.map((c, i) => <EmissionsCategory key={i} emissionsCategory={c} headingClicked={headingClicked} showSections={showSections} categoryRefData={categoryRefData} hasParent={true} />)}

            {isShow &&
                emissionsCategory.type === "EntericMethaneTotal" &&
                productionResults &&
                Object.keys(productionResults).map((k, i) => (
                    <tr id={k + "-row"} className={`tr-nested`} key={i}>
                        <td colSpan={2} id={utils.strToId(k + "-label")}>
                            {((productionTypeRefData && productionTypeRefData.find((r) => r.value === k)) || { text: k }).text}
                        </td>
                        <td id={utils.strToId(k + "-value")} className="u-textRight">
                            {productionResults[k].entericMethane}
                        </td>
                    </tr>
                ))}
        </React.Fragment>
    );
};

const CreateEmissionsData = (emissionsCategory, categoryRefData, colour) => {
    const label = categoryRefData.find((r) => r.value === emissionsCategory.type) || { text: "Unknown" };
    return { colour: colour, label: label.text, value: emissionsCategory.value };
};

const EmissionsGraph = ({ results, categoryRefData }) => {
    const emissionsData = [].concat(
        results.methaneEmissions.map((e) => CreateEmissionsData(e, categoryRefData, methaneColour.hex)),
        results.n2OEmissions.map((e) => CreateEmissionsData(e, categoryRefData, n2oColour.hex)),
        results.cO2Emissions.map((e) => CreateEmissionsData(e, categoryRefData, co2Colour.hex))
    );
    const pieData = [results.methaneEmissions.reduce((t, c) => (t += c.value), 0), results.n2OEmissions.reduce((t, c) => (t += c.value), 0), results.cO2Emissions.reduce((t, c) => (t += c.value), 0)];
    const totalPie = pieData.reduce((t, d) => (t += d || 0), 0);
    if (totalPie > 0) {
        pieData[0] = ((pieData[0] || 0) / totalPie) * 100;
        pieData[1] = ((pieData[1] || 0) / totalPie) * 100;
        pieData[2] = ((pieData[2] || 0) / totalPie) * 100;
    }

    const pData = {
        labels: ["Methane", "N2O", "CO2"],
        datasets: [
            {
                fill: "origin",
                lineTension: 0,
                backgroundColor: [methaneColour.hex, n2oColour.hex, co2Colour.hex],
                labels: ["%", "%", "%"],
                data: pieData.map((d) => utils.round(d, 1)),
            },
        ],
    };

    const nData = {
        labels: emissionsData.map((d) => d.label),
        datasets: [
            {
                fill: "origin",
                lineTension: 0,
                backgroundColor: emissionsData.map((d) => d.colour),
                borderColor: "black",
                pointRadius: 0,
                data: emissionsData.map((d) => utils.round(d.value, 2)),
            },
        ],
    };

    const options = {
        scales: {
            x: { display: true, ticks: { autoSkip: false } },
            y: {
                ticks: { beginAtZero: true },
                display: true,
                title: { display: true, text: "CO2-e (kg/ha/yr)" },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
        },
    };

    const pOptions = {
        plugins: {
            legend: {
                display: true,
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        return `${tooltipItem.raw.toLocaleString()} %`;
                    },
                },
            },
        },
        responsive: false,
        maintainAspectRatio: false,
    };

    return (
        <>
            <Bar data={nData} height={175} options={options} />
            <div className="u-flex u-flexJustifyCenter">
                <Pie data={pData} options={pOptions} height={400} width={400} />
            </div>
        </>
    );
};

class GHGReports extends Component {
    constructor(props) {
        super(props);
        this.state = { showSections: {}, displaycumulative: "cumulative" };
    }

    componentDidMount() {
        const { budget, getForestCarbon, carbonSequestrationStatus } = this.props;
        const { blocks = [] } = budget;
        if (carbonSequestrationStatus === "Pending" && blocks.some((b) => b.type === domain.BlockType.NonProductiveTreesAndScrub && b.forests && b.forests.length > 0)) {
            getForestCarbon(budget.farmId, budget.id);
        }
    }

    componentWillUnmount() {
        const { clearForestCarbon, carbonSequestrationStatus } = this.props;
        if (carbonSequestrationStatus !== "Pending") {
            clearForestCarbon();
        }
    }

    headingClicked(type) {
        const { showSections = {} } = this.state;
        showSections[type] = !(showSections[type] || false);
        this.setState({ showSections: showSections });
    }

    render() {
        const { farm, budget, refData = {}, carbonSequestration = [] } = this.props;
        let latestResults = budget.currentResults || {};
        const { blocks = [] } = budget;
        const csBlocks = blocks.filter((b) => b.type === domain.BlockType.NonProductiveTreesAndScrub && b.forests && b.forests.length > 0);
        const { forestType = [] } = refData;
        let budgetForestArea = 0;
        let budgetBlockArea = 0;
        let ghgEmissionsTotal = 0;
        let currentCarbonStock = 0;
        let bestCarbonStock = 0;
        let bestCarbonStockYear = 0;
        if (latestResults && latestResults.ghgResults) {
            ghgEmissionsTotal += latestResults.ghgResults.cO2EmissionsTotal || 0;
            ghgEmissionsTotal += latestResults.ghgResults.methaneEmissionsTotal || 0;
            ghgEmissionsTotal += latestResults.ghgResults.n2OEmissionsTotal || 0;
        }
        const productiveBlocks = blocks.filter((b) => b.isProductive && b.type !== "FodderCrop");
        const productiveBlockArea = productiveBlocks.reduce((total, block) => (total += block.areaInHectares), 0);
        const farmEmissionsTotal = utils.round((productiveBlockArea * ghgEmissionsTotal) / 1000, 2);

        const budgetBarNData = {
            labels: [],
            datasets: [],
        };

        const totalsBarNData = {
            labels: [],
            datasets: [],
        };

        const lineOptions = {
            scales: {
                y: {
                    title: {
                        display: true,
                        text: `${this.state.displaycumulative === "cumulative" ? "Cumulative" : "Actual"} carbon stock total tonnes (indicative)`,
                    },
                },
                x: {
                    title: {
                        display: true,
                        text: "Age",
                    },
                },
            },
            datasets: {
                line: {
                    tension: 0.4,
                },
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        title: (tooltipItem) => {
                            const { dataset } = tooltipItem[0];
                            return `${dataset.label} - Age: ${tooltipItem[0].label}`;
                        },
                        label: (tooltipItem) => {
                            return `${this.state.displaycumulative === "cumulative" ? "Cumulative" : "Actual"}: ${tooltipItem.formattedValue} tonnes`;
                        },
                        afterLabel: (tooltipItem, data) => {
                            if (tooltipItem.index > 0 && this.state.displaycumulative === "cumulative") {
                                const { dataset } = tooltipItem;
                                const val = dataset.data[tooltipItem.dataIndex] - dataset.data[tooltipItem.dataIndex - 1];
                                return `Annual: ${utils.round(val, 2)} tonnes`;
                            }
                        },
                    },
                },
            },
        };

        const harvestOptions = {
            scales: {
                y: {
                    title: {
                        display: true,
                        text: `${this.state.displaycumulative === "cumulative" ? "Cumulative" : "Actual"} carbon stock total tonnes (indicative)`,
                    },
                },
                x: {
                    title: {
                        display: true,
                        text: "Years since harvest",
                    },
                },
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        title: (tooltipItem) => {
                            const { dataset } = tooltipItem[0];
                            return `${dataset.label} - ${tooltipItem[0].label}`;
                        },
                        label: (tooltipItem) => {
                            return `${this.state.displaycumulative === "cumulative" ? "Cumulative" : "Actual"}: ${tooltipItem.formattedValue} tonnes`;
                        },
                        afterLabel: (tooltipItem) => {
                            if (tooltipItem.dataIndex > 0 && this.state.displaycumulative === "cumulative") {
                                const { dataset } = tooltipItem;
                                const val = dataset.data[tooltipItem.dataIndex] - dataset.data[tooltipItem.dataIndex - 1];
                                return `Annual: ${utils.round(val, 2)} tonnes`;
                            }
                        },
                    },
                },
            },
        };

        const barOptions = {
            scales: {
                x: {
                    stacked: true,
                    title: {
                        display: true,
                        text: "Years",
                    },
                },
                y: {
                    stacked: true,
                    title: {
                        display: true,
                        text: `${this.state.displaycumulative === "cumulative" ? "Cumulative" : "Actual"} carbon stock total tonnes (indicative)`,
                    },
                },
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        title: (tooltipItem) => {
                            const { dataset } = tooltipItem[0];
                            return `${dataset.label} - Year: ${tooltipItem[0].label}`;
                        },
                        label: (tooltipItem) => {
                            if (tooltipItem.datasetIndex === 0) {
                                return `${this.state.displaycumulative === "cumulative" ? "Cumulative" : "Actual"}: ${tooltipItem.formattedValue} tonnes`;
                            }
                        },
                        afterLabel: (tooltipItem) => {
                            if (tooltipItem.dataIndex > 0 && tooltipItem.datasetIndex === 0 && this.state.displaycumulative === "cumulative") {
                                const { dataset } = tooltipItem;
                                const val = dataset.data[tooltipItem.dataIndex] - dataset.data[tooltipItem.dataIndex - 1];
                                return `Annual: ${utils.round(val, 2)} tonnes`;
                            }
                        },
                    },
                },
            },
        };

        const animalTypes = ["Dairy", "DairyReplacements", "Sheep", "Beef", "DairyGrazing", "Deer", "DairyGoats", "OutdoorPigs", "OtherAnimals"];
        const animalFootprint = latestResults.ghgResults ? Object.keys(latestResults.ghgResults.productionResults).filter((k) => animalTypes.includes(k) && latestResults.ghgResults.productionResults[k].cO2Source > 0) : [];
        const nonAnimalFootprint = latestResults.ghgResults ? Object.keys(latestResults.ghgResults.productionResults).filter((k) => !animalTypes.includes(k) && latestResults.ghgResults.productionResults[k].cO2Source > 0) : [];

        return (
            <>
                {latestResults && latestResults.ghgResults && (latestResults.ghgResults.cO2EmissionsTotal !== 0 || latestResults.ghgResults.methaneEmissionsTotal !== 0 || latestResults.ghgResults.n2OEmissionsTotal !== 0) && (
                    <>
                        <FeatureTracker feature={FEATURES.GHG_REPORT} farm={farm} />
                        <Alert type="info" text={<span>You can print this page by selecting print under the browser menu. Landscape is recommended for this report.</span>} />
                        <Panel title="Greenhouse gases" skyBlue info="The 'Emissions by source' table and graph displays the average GHG emissions as CO2 equivalents (CO2-e) in kg/ha/yr for the farm. Clicking on the blue rows displays a further breakdown of that source. The 'Total farm emissions' table shows the total farm emissions in CO2 equivalents (CO2-e) tonnes per year broken down by methane, N2O and CO2">
                            <PanelBody className="u-print-noborder">
                                <div className="h2 u-pb-sm">Emissions by source</div>
                                <div className="Grid-cell u-lg-width1of3">
                                    <div className="Table u-pos-rel u-overflow-hidden">
                                        <Watermark />
                                        <table className="u-tbl-fix">
                                            <thead>
                                                <tr>
                                                    <th colSpan={2}>Methane</th>
                                                    <th className="u-textRight">
                                                        <small>CO2-e kg/ha/yr</small>
                                                        <h4 className="u-mt-0">{latestResults.ghgResults.methaneEmissionsTotal}</h4>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {latestResults.ghgResults.methaneEmissions.map((c, i) => (
                                                    <EmissionsCategory key={i} colour={methaneColour.name} emissionsCategory={c} headingClicked={(type) => this.headingClicked(type)} showSections={this.state.showSections} categoryRefData={refData.ghgEmissionTypes} productionResults={latestResults.ghgResults.productionResults} productionTypeRefData={refData.productionTypes} />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="Table u-pos-rel u-overflow-hidden">
                                        <Watermark />
                                        <table className="u-tbl-fix">
                                            <thead>
                                                <tr>
                                                    <th colSpan={2}>N2O</th>
                                                    <th className="u-textRight">
                                                        <small>CO2-e kg/ha/yr</small>
                                                        <h4 className="u-mt-0">{latestResults.ghgResults.n2OEmissionsTotal}</h4>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {latestResults.ghgResults.n2OEmissions.map((c, i) => (
                                                    <EmissionsCategory key={i} colour={n2oColour.name} emissionsCategory={c} headingClicked={(type) => this.headingClicked(type)} showSections={this.state.showSections} categoryRefData={refData.ghgEmissionTypes} />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="Table u-page-break u-pos-rel u-overflow-hidden">
                                        <Watermark />
                                        <table className="u-tbl-fix">
                                            <thead>
                                                <tr>
                                                    <th colSpan={2}>CO2</th>
                                                    <th className="u-textRight">
                                                        <small>CO2-e kg/ha/yr</small> <h4 className="u-mt-0">{latestResults.ghgResults.cO2EmissionsTotal}</h4>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {latestResults.ghgResults.cO2Emissions.map((c, i) => (
                                                    <EmissionsCategory key={i} colour={co2Colour.name} emissionsCategory={c} headingClicked={(type) => this.headingClicked(type)} showSections={this.state.showSections} categoryRefData={refData.ghgEmissionTypes} />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="Grid-cell u-lg-width2of3 u-page-break">
                                    <div style={{ paddingTop: "0px", paddingLeft: "30px" }} className="u-pos-rel u-overflow-hidden">
                                        <div className="u-pt-lg">
                                            <Watermark />
                                            <EmissionsGraph results={latestResults.ghgResults} categoryRefData={refData.ghgEmissionTypes} />
                                        </div>
                                    </div>
                                </div>
                                <div className="u-pb-sm u-pt-xxl">
                                    <div className="h2 u-pt-md">Total farm emissions</div>
                                </div>
                                <div className="Grid-cell">
                                    {latestResults.ghgResults.reportingArea && (
                                        <div className="Table u-mt-0 u-mb-md">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Total farm emissions in tonnes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>
                                                            <div className="HeroValue">
                                                                <div className="HeroValue-item">
                                                                    <h5>Methane GHG emissions</h5>
                                                                    <div className="u-flex u-flexColumn">
                                                                        <span id="methane-emissions" className="h1">
                                                                            {utils.round((latestResults.ghgResults.methaneEmissionsTotal * latestResults.ghgResults.reportingArea) / 1000, 1)} <small>CO2-e tonnes/yr</small>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="HeroValue-item">
                                                                    <h5>N2O GHG emissions</h5>
                                                                    <div className="u-flex u-flexColumn">
                                                                        <span id="n2O-emissions" className="h1">
                                                                            {utils.round((latestResults.ghgResults.n2OEmissionsTotal * latestResults.ghgResults.reportingArea) / 1000, 1)} <small>CO2-e tonnes/yr</small>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="HeroValue-item">
                                                                    <h5>CO2 GHG emissions</h5>
                                                                    <div className="u-flex u-flexColumn">
                                                                        <span id="cO2-emissions" className="h1">
                                                                            {utils.round((latestResults.ghgResults.cO2EmissionsTotal * latestResults.ghgResults.reportingArea) / 1000, 1)} <small>CO2-e tonnes/yr</small>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="HeroValue-item">
                                                                    <h5>Total GHG emissions</h5>
                                                                    <div className="u-flex u-flexColumn">
                                                                        <span id="total-emissions" className="h1">
                                                                            {utils.round((latestResults.ghgResults.total * latestResults.ghgResults.reportingArea) / 1000, 1)} <small>CO2-e tonnes/yr</small>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </PanelBody>
                        </Panel>
                    </>
                )}
                {latestResults && latestResults.ghgResults && latestResults.ghgResults.productionResults && latestResults.ghgResults.total !== 0 && (
                    <Panel title="Emissions by enterprise and area" skyBlue info="The following table lists CO2 equivalent (CO2-e) footprints in KGs on a per farm enterprise.">
                        <PanelBody>
                            <Grid>
                                {animalFootprint.length > 0 && (
                                    <GridCell className="u-md-width1of1 u-lg-width1of2 u-pb-md">
                                        <div className="h3 u-mb-sm">Animals</div>
                                        <div className="Table">
                                            <Watermark />
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th width="50%">Enterprise</th>
                                                        <th className="u-textRight">CO2-e kg per unit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {animalFootprint.map((k, i) => (
                                                        <tr key={i} id={`${k}-row`}>
                                                            <td>{((refData.productionTypes && refData.productionTypes.find((r) => r.value === k)) || { text: k }).text}</td>
                                                            <td className="u-textRight" id={`${k}_cO2Footprint`}>
                                                                {latestResults.ghgResults.productionResults[k].cO2Footprint.toLocaleString() + (k === "Dairy" ? " per cow" : k === "FruitCrop" || k === "VegetableArable" || k === "ExportedSupplement" ? " per ha" : " per SU")}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </GridCell>
                                )}
                                {nonAnimalFootprint.length > 0 && (
                                    <GridCell className="u-md-width1of1 u-lg-width1of2 u-pb-md">
                                        <div className="h3 u-mb-sm">Crops and supplements</div>
                                        <div className="Table u-mt-0">
                                            <Watermark />
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th width="50%">Enterprise</th>
                                                        <th className="u-textRight">CO2-e kg per unit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {nonAnimalFootprint.map((k, i) => (
                                                        <tr key={i} id={`${k}-row`}>
                                                            <td>{((refData.productionTypes && refData.productionTypes.find((r) => r.value === k)) || { text: k }).text}</td>
                                                            <td className="u-textRight" id={`${k}_cO2Footprint`}>
                                                                {latestResults.ghgResults.productionResults[k].cO2Footprint.toLocaleString() + (k === "Dairy" ? " per cow" : k === "FruitCrop" || k === "VegetableArable" || k === "ExportedSupplement" ? " per ha" : " per SU")}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </GridCell>
                                )}
                            </Grid>
                        </PanelBody>
                    </Panel>
                )}
                <Panel title="Carbon stock" skyBlue>
                    <PanelBody>
                        {carbonSequestration.length > 0 && (
                            <>
                                <Alert
                                    type="info"
                                    text="The carbon stock report below has been
                                developed based on lookup tables for post 1989 forest land by MPI. Pre-calculated values in look-
                                up tables are not specific to an individual farm. For further information on Carbon Look-up tables,
                                please contact MPI. email climatechange@mpi.govt.nz or call 0800 CLIMATE (0800 25 46 28)
                                and select option 3."
                                />

                                <Alert
                                    type="warning"
                                    text={
                                        <div>
                                            <b>Disclaimer:</b>The information in this report has no statutory or regulatory effect and is of a guidance nature only. The information should not be relied on as a substitute for the wording of the Climate Change Response Act 2002. While every effort has been made to ensure the information in this report is accurate, Overseer Ltd does not accept any responsibility or liability for error of fact, omission, interpretation or opinion that may be present, nor for the consequences of any decisions based on this information.{" "}
                                        </div>
                                    }
                                />

                                <Alert
                                    type="info"
                                    text={
                                        <div>
                                            This report shows the potential for tree blocks accumulating carbon stocks over time - <i>cumulative</i>, as well as the potential for <i>annual carbon stocks</i>. You can view the annual carbon stocks information by hovering on the graph on year one and subsequent years. Alternatively, toggle between <i>cumulative</i> and <i>annual carbon stocks</i> information by selecting a reporting method. You will see how much additional carbon stocks tree blocks will have sequestered in each twelve-month period. From here, you can assess how changes in carbon stocks might impact your farm emissions profile. For more information on the carbon stock tool &nbsp;
                                            <ZendeskLink url="https://support.overseer.org.nz/hc/en-us/articles/900000922343" target="_blank" rel="noopener noreferrer" id="lnk-open-guide" title="click here." />{" "}
                                        </div>
                                    }
                                />
                            </>
                        )}

                        {carbonSequestration.length === 0 ? (
                            <div className="Table u-mt-md">
                                <div className="Tile-body">
                                    <div className="Tile-body-message">
                                        <h3>You do not have any forests set up for the carbon stock tool. Please edit your tree blocks and add forests to view details for your farm.</h3>
                                        <Link to={`/app/farm/${farm.id}/analysis/${budget.id}`} id="create-tree-block" className="IconLink--arrow-plus Button Button--secondary u-mt-md">
                                            Add forests
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <SelectPack meta={{ nrf: true }} onChange={(e) => this.setState({ displaycumulative: e.target.value })} value={this.state.displaycumulative} autoFocus={true} id="custom_soil_group" label="Reporting method" dataWidth="25" requiredLabel={false}>
                                    <option value="cumulative">Cumulative</option>
                                    <option value="actual">Annual</option>
                                </SelectPack>
                            </div>
                        )}
                        {carbonSequestration.length > 0 && (
                            <>
                                {csBlocks.map((b, i) => {
                                    const { forests = [] } = b;
                                    //let blockArea = 0;
                                    const blockNData = {
                                        labels: [],
                                        datasets: [],
                                    };
                                    budgetBlockArea += b.areaInHectares;
                                    const blockIcon = utils.getBlockIcon(b.type);
                                    return (
                                        <React.Fragment key={i}>
                                            <div className="h2 u-pt-lg">
                                                <div className="FarmTitle">
                                                    <img className="FarmTitle-icon" src={blockIcon} alt="Block" />
                                                    <div className="FarmTitle-heading">
                                                        <span className="FarmTitle-name" title={b.blockName}>
                                                            {b.name}
                                                        </span>
                                                        <div className="FarmTitle-sub">Trees and scrub - {b.areaInHectares}ha</div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="Table u-mt-sm">
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th>Name/Type</th>
                                                            <th>Age</th>
                                                            <th>Percentage of block</th>
                                                            <th>Area</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {forests.map((f, x) => {
                                                            const forestResults = carbonSequestration.find((fr) => fr.blockId === b.id && fr.forestId === f.id) || {};
                                                            const area = utils.round((b.areaInHectares * f.percentage) / 100 || 1, 1);
                                                            //blockArea += area
                                                            budgetForestArea += f.isHarvested ? 0 : area;
                                                            const { carbonStockValues = [] } = forestResults;
                                                            const carbonArray = this.state.displaycumulative === "cumulative" ? carbonStockValues.map((cs) => utils.round(cs * area, 2)) : carbonStockValues.map((cs, i) => (i === 0 ? 0 : utils.round((cs - carbonStockValues[i - 1]) * area, 2)));

                                                            if (this.state.displaycumulative !== "cumulative" && carbonStockValues.length > 0) {
                                                                carbonArray[0] = utils.round((carbonStockValues[0] - forestResults.previousUntilForestAge) * area, 2);
                                                            }

                                                            const forestDataset = {
                                                                label: `${f.name || utils.valueToText(forestType, f.forestType)} ${f.isHarvested ? " - (residual)" : ""}`,
                                                                fill: false,
                                                                lineTension: 0.1,
                                                                backgroundColor: utils.getGraphColour(x),
                                                                borderColor: utils.getGraphColour(x),
                                                                data: carbonArray,
                                                            };

                                                            const nData = {
                                                                labels: carbonStockValues.map((fr, ix) => (f.isHarvested ? f.numberOfYearsSinceHarvested : f.forestAge) + ix),
                                                                datasets: [forestDataset],
                                                            };

                                                            blockNData.datasets.unshift(forestDataset);

                                                            while (blockNData.labels.length < nData.labels.length) {
                                                                blockNData.labels.push(blockNData.labels.length);
                                                            }
                                                            while (budgetBarNData.labels.length < nData.labels.length) {
                                                                budgetBarNData.labels.push(budgetBarNData.labels.length);
                                                            }
                                                            while (totalsBarNData.labels.length < nData.labels.length) {
                                                                totalsBarNData.labels.push(totalsBarNData.labels.length);
                                                            }

                                                            let budgetDataSet = budgetBarNData.datasets.find((bd) => bd.id === b.id);

                                                            const names = csBlocks.filter((cs) => cs.name === b.name);
                                                            const label = names.length > 1 ? `${b.name}_${csBlocks.indexOf(b)}` : b.name;

                                                            if (!budgetDataSet) {
                                                                budgetDataSet = {
                                                                    id: b.id,
                                                                    label: label,
                                                                    fill: false,
                                                                    isHarvested: f.isHarvested,
                                                                    type: "bar",
                                                                    stacked: true,
                                                                    lineTension: 0.1,
                                                                    backgroundColor: utils.getGraphColour(i),
                                                                    borderColor: utils.getGraphColour(i),
                                                                    data: carbonArray,
                                                                };
                                                                budgetBarNData.datasets.unshift(budgetDataSet);
                                                            } else {
                                                                budgetDataSet.data = budgetBarNData.labels.map((l, li) => {
                                                                    let sum = 0;
                                                                    if (budgetDataSet.data.length > li) {
                                                                        sum += budgetDataSet.data[li];
                                                                    }
                                                                    if (carbonArray.length > li) {
                                                                        sum += carbonArray[li];
                                                                    }
                                                                    return utils.round(sum, 2);
                                                                });
                                                            }
                                                            //block total line
                                                            if (forests.length === x + 1) {
                                                                blockNData.datasets.unshift({
                                                                    label: "Block total",
                                                                    type: "line",
                                                                    fill: false,
                                                                    stacked: false,
                                                                    lineTension: 0.1,
                                                                    backgroundColor: utils.getGraphColour(10),
                                                                    borderColor: utils.getGraphColour(10),
                                                                    data: budgetBarNData.labels.map((l, li) => {
                                                                        return utils.round(
                                                                            blockNData.datasets.reduce((total, dataset) => (dataset.data.length > li ? (total += dataset.data[li]) : total), 0),
                                                                            2
                                                                        );
                                                                    }),
                                                                });
                                                            }

                                                            //budget totals line
                                                            if (csBlocks.length === i + 1 && forests.length === x + 1) {
                                                                const ds = {
                                                                    label: "Farm total",
                                                                    type: "line",
                                                                    fill: false,
                                                                    stacked: false,
                                                                    lineTension: 0.1,
                                                                    backgroundColor: utils.getGraphColour(10),
                                                                    borderColor: utils.getGraphColour(10),
                                                                    data: budgetBarNData.labels.map((l, li) => {
                                                                        return utils.round(
                                                                            budgetBarNData.datasets.reduce((total, dataset) => (dataset.data.length > li ? (total += dataset.data[li]) : total), 0),
                                                                            2
                                                                        );
                                                                    }),
                                                                };
                                                                budgetBarNData.datasets.unshift(ds);

                                                                if (ds.data.length > 0) {
                                                                    currentCarbonStock = ds.data[0];
                                                                }
                                                                bestCarbonStock = ds.data.reduce((best, current) => (current > best ? current : best), 0);
                                                                bestCarbonStockYear = ds.data.indexOf(bestCarbonStock);

                                                                const dsOffset = {
                                                                    label: "Offset",
                                                                    type: "line",
                                                                    fill: false,
                                                                    stacked: false,
                                                                    lineTension: 0.1,
                                                                    backgroundColor: utils.getGraphColour(1),
                                                                    borderColor: utils.getGraphColour(1),
                                                                    data: ds.data.map((v) => utils.round(farmEmissionsTotal - v, 2)),
                                                                };

                                                                totalsBarNData.datasets.unshift(dsOffset);
                                                            }

                                                            const forestUiId = `${b.id}_${f.id}`;
                                                            const showForestGraph = this.state[forestUiId];

                                                            return (
                                                                <React.Fragment key={x}>
                                                                    <tr>
                                                                        <td>
                                                                            <ActionLink className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween" onClick={() => this.setState({ [forestUiId]: !showForestGraph })}>
                                                                                {f.name || utils.valueToText(forestType, f.forestType)}
                                                                                {carbonSequestration.length > 0 && <i className={`icon icon-arrow-${showForestGraph ? "up" : "down"}`}></i>}
                                                                            </ActionLink>
                                                                        </td>
                                                                        <td>
                                                                            {f.forestAge} <small>years</small>
                                                                        </td>
                                                                        <td>
                                                                            {f.percentage}
                                                                            <small>%</small>
                                                                        </td>
                                                                        <td>
                                                                            {utils.round(area, 2)}
                                                                            <small>ha</small>
                                                                        </td>
                                                                    </tr>
                                                                    {carbonSequestration.length > 0 && showForestGraph && (
                                                                        <tr>
                                                                            <td colSpan="4">
                                                                                <div className="u-pt-md">
                                                                                    <Line data={nData} legend={{ display: true }} height={75} options={f.isHarvested ? harvestOptions : lineOptions} />
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {blockNData.datasets.length > 2 && (
                                                <div className="u-pt-md">
                                                    <Bar data={blockNData} options={barOptions} />
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                {budgetBarNData.datasets.length > 2 && (
                                    <React.Fragment>
                                        <div className="h2 u-mb-sm u-mt-lg">{this.state.displaycumulative === "cumulative" ? "Cumulative" : "Annual"} carbon stock totals</div>
                                        {false && (
                                            <div className="Table u-mt-md u-mb-md">
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th>Totals</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>
                                                                <div className="HeroValue">
                                                                    <div className="HeroValue-item">
                                                                        <h5>Block area</h5>
                                                                        <p className="HeroValue-item-number">
                                                                            {budgetBlockArea}
                                                                            <small>ha</small>
                                                                        </p>
                                                                    </div>
                                                                    <div className="HeroValue-item">
                                                                        <h5>Forest area</h5>
                                                                        <p className="HeroValue-item-number">
                                                                            {utils.round(budgetForestArea, 1)}
                                                                            <small>ha</small>
                                                                        </p>
                                                                    </div>
                                                                    <div className="HeroValue-item">
                                                                        <h5>Forest percentage</h5>
                                                                        <p className="HeroValue-item-number">
                                                                            {utils.round((budgetForestArea / budgetBlockArea) * 100, 1)}
                                                                            <small>%</small>
                                                                        </p>
                                                                    </div>
                                                                    <div className="HeroValue-item">
                                                                        <h5>Current carbon stock</h5>
                                                                        <p className="HeroValue-item-number">
                                                                            {utils.round(currentCarbonStock, 1).toLocaleString()} <small>tonnes</small>
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                        <Bar data={budgetBarNData} options={barOptions} />
                                    </React.Fragment>
                                )}
                                {false && (
                                    <React.Fragment>
                                        <div className="h2 u-mb-sm">Tree blocks carbon sequestration (indicative)</div>
                                        <div className="Table u-mt-md u-mb-md">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Totals</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>
                                                            <div className="HeroValue">
                                                                <div className="HeroValue-item">
                                                                    <h5>Farm CO2 equivalent emissions</h5>
                                                                    <div className="u-flex u-flexColumn">
                                                                        <span className="h1">
                                                                            {utils.round(farmEmissionsTotal, 1).toLocaleString()} <small>tonnes</small>
                                                                        </span>
                                                                        <span className="u-textNoWrap">
                                                                            {utils.round(ghgEmissionsTotal / 1000, 2)} tonnes/ha * {utils.round(productiveBlockArea, 2)}ha (productive area)
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="HeroValue-item">
                                                                    <h5>Farm CO2 equivalent emissions - carbon stock (year 0)</h5>
                                                                    <div className="u-flex u-flexColumn">
                                                                        <span className="h1">
                                                                            {utils.round(farmEmissionsTotal - currentCarbonStock, 0).toLocaleString()} <small>tonnes</small>
                                                                        </span>
                                                                        <span className="u-textNoWrap">
                                                                            {utils.round(farmEmissionsTotal, 1).toLocaleString()} tonnes (farm emissions) - {utils.round(currentCarbonStock, 1).toLocaleString()} tonnes (carbon stock)
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="HeroValue-item">
                                                                    <h5>Farm CO2 equivalent emissions - carbon stock (year 30)</h5>
                                                                    <div className="u-flex u-flexColumn">
                                                                        <span className="h1">
                                                                            {utils.round(farmEmissionsTotal - bestCarbonStock, 0).toLocaleString()} <small>tonnes</small>
                                                                        </span>
                                                                        <span className="u-textNoWrap">
                                                                            {utils.round(farmEmissionsTotal, 1).toLocaleString()} tonnes (farm emissions) - {utils.round(bestCarbonStock, 1).toLocaleString()} tonnes (carbon stock) at year {bestCarbonStockYear}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </React.Fragment>
                                )}
                                {false && <Bar data={totalsBarNData} options={barOptions} />}
                            </>
                        )}
                    </PanelBody>
                </Panel>
            </>
        );
    }
}

export default connect(
    (state) => {
        return {
            carbonSequestration: state.reportSupplements.carbonSequestration,
            carbonSequestrationStatus: state.reportSupplements.carbonSequestrationStatus,
        };
    },
    { getForestCarbon, clearForestCarbon }
)(GHGReports);
